import { Conversation } from '../types/chat';
import { EventBus } from '../utils/eventBus';
import { TabsService } from '../services/tabs';
import { createElement, createButton, clearElement, isMobileDevice } from '../utils/dom';

export class TabBar {
  private container: HTMLElement | null = null;
  private conversations: Conversation[] = [];
  private activeConversationId: string | null = null;

  constructor(
    private eventBus: EventBus,
    private tabsService: TabsService
  ) {
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.eventBus.on('state:updated', (state) => {
      this.conversations = Object.values(state.conversations as Record<string, Conversation>).sort(
        (a, b) => b.updatedAt - a.updatedAt
      );
      this.activeConversationId = state.activeConversationId;
      this.render();
    });

    this.eventBus.on('conversation:created', () => {
      // State will be updated via state:updated event
    });

    this.eventBus.on('conversation:deleted', () => {
      // State will be updated via state:updated event
    });

    this.eventBus.on('activeConversation:changed', (id) => {
      this.activeConversationId = id;
      this.render();
    });
  }

  mount(container: HTMLElement): void {
    this.container = container;
    this.conversations = this.tabsService.getAllTabs();
    this.activeConversationId = this.tabsService.getActiveTab()?.id ?? null;
    this.render();
  }

  private render(): void {
    if (!this.container) return;

    clearElement(this.container);

    // Tab list
    const tabList = createElement('div', 'tab-list');

    this.conversations.forEach(conversation => {
      const tab = this.renderTab(conversation);
      tabList.appendChild(tab);
    });

    // New tab button (Chrome-style) - inside tab list
    const newTabButton = createButton('+', 'btn-new-tab', () => {
      this.tabsService.createTab();
    });
    newTabButton.title = 'New tab';
    tabList.appendChild(newTabButton);

    this.container.appendChild(tabList);
  }

  private renderTab(conversation: Conversation): HTMLElement {
    const isActive = conversation.id === this.activeConversationId;

    const tab = createElement('div', `tab ${isActive ? 'tab-active' : ''}`);
    const handler = (e: MouseEvent | TouchEvent) => {
      // Don't switch if clicking close button
      if ((e.target as HTMLElement).closest('.tab-close')) return;
      if (conversation.id !== this.activeConversationId) {
        this.tabsService.switchTab(conversation.id);
      }
    }

    if (isMobileDevice()) {
      tab.addEventListener('touchstart', handler);
    } else {
      tab.addEventListener('click', handler);
    }

    const title = createElement('span', 'tab-title', conversation.title);
    tab.appendChild(title);

    // Close button (don't show if it's the only tab)
    if (this.conversations.length > 1) {
      const closeButton = createButton('✕', 'tab-close', () => {
        this.tabsService.closeTab(conversation.id);
      });
      tab.appendChild(closeButton);
    }

    return tab;
  }

  destroy(): void {
    this.eventBus.off('state:updated');
    this.eventBus.off('conversation:created');
    this.eventBus.off('conversation:deleted');
    this.eventBus.off('activeConversation:changed');
  }
}
